import { admissionState, institutions, portalLink } from './admissionRouting';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getAdmissionRounds } from "services/content";
import { loadDraftAccess } from "services/admissions";
import {
  PortalShell,
  Surface,
  Button,
  LangToggle,
  useT,
  useFontClass,
  toBn,
} from "components/admissions/portalUi";

/**
 * Admission landing.
 *
 * One panel answers the questions a parent actually arrives with: which
 * institution, which session, which classes, by when, and is it open — all read
 * from the admission round staff control, never hard-coded. Start is the one
 * prominent action; Continue sits beside it for anyone returning.
 *
 * The "keep these ready" checklist and the note about saving come before any
 * field, so nobody starts filling a long form only to discover halfway that
 * they need a certificate from a drawer.
 */

const INSTITUTION_LABEL = {
  kcns: ["কেসিএনএস — নার্সারি ও প্রাথমিক", "KCNS — Nursery & Primary"],
  kcgs: ["কেসিজিএস — স্কুল ও কলেজ", "KCGS — School & College"],
  honors: ["কেসিসিডব্লিউসি — অনার্স", "KCCWC — Honours"],
};

const fmtDate = (v, lang) =>
  v
    ? new Date(v).toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

function InfoRow({ label, children }) {
  const font = useFontClass();
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3 border-b border-line last:border-b-0">
      <span className={`${font} text-[14px] text-muted sm:w-[170px] sm:shrink-0`}>{label}</span>
      <span className={`${font} text-[16px] text-ink font-medium`}>{children}</span>
    </div>
  );
}

export default function PortalLanding() {
  const [query] = useSearchParams();
  const institution = query.get("institution");
  const t = useT();
  const font = useFontClass();
  const navigate = useNavigate();
  const { data: rounds, loading, error } = useResource(getAdmissionRounds);
  // A draft saved on this device lets Continue skip straight back in.
  const draft = loadDraftAccess();

  const round = (rounds || []).find((r) => r.institution === institution);
  const closesAt = round?.closesAt ? new Date(round.closesAt) : null;
  const state = admissionState(round);
  const isOpen = state === "open";
  const lang = font === "font-bn" ? "bn" : "en";

  const checklist = [
    ["শিক্ষার্থীর পাসপোর্ট সাইজের ছবি", "Student's passport-size photo"],
    ["জন্ম নিবন্ধন সনদ", "Birth registration certificate"],
    ["আগের প্রতিষ্ঠানের ছাড়পত্র (বদলির ক্ষেত্রে)", "Transfer certificate (if transferring)"],
    ["সর্বশেষ পরীক্ষার ফলাফল / মার্কশিট", "Most recent exam result or marksheet"],
    ["অভিভাবকের মোবাইল নম্বর", "Guardian's mobile number"],
  ];

  if (!institutions[institution]) return <PortalShell><LangToggle /><h1 className="text-3xl font-bold my-6">{t('প্রতিষ্ঠান বেছে নিন', 'Choose your institution')}</h1><div className="space-y-4">{Object.entries(institutions).map(([key, label]) => <Link key={key} className="block rounded-xl border border-line bg-white p-5 text-green font-semibold" to={portalLink(key)}>{label} →</Link>)}</div><Link className="block mt-6 underline" to="/admissions-portal/continue">{t('আবেদন চালিয়ে যান', 'Continue or track an application')}</Link></PortalShell>;
  return (
    <PortalShell>
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <Link
          to="/"
          className={`${font} text-[14px] text-muted no-underline hover:text-ink min-h-[44px] inline-flex items-center`}
        >
          ← {t("ওয়েবসাইটে ফিরুন", "Back to website")}
        </Link>
        <LangToggle />
      </div>

      <h1
        className={`${font} font-serif text-[30px] sm:text-[38px] font-bold text-green-dark leading-tight mb-2`}
      >
        {t("ভর্তির আবেদন", "Admission application")}
      </h1>
      <p className={`${font} text-[17px] text-muted leading-relaxed mb-7`}>
        {t(
          "অনলাইনে আবেদন করুন, ফরম প্রিন্ট করে স্বাক্ষরসহ বিদ্যালয়ে জমা দিন।",
          "Apply online, then print the form, sign it and submit it to the school."
        )}
      </p>

      {/* ---------------------------------------------- the one status panel */}
      {loading ? (
        <Surface className="p-6 mb-7">
          <span className={`${font} text-[15px] text-muted`}>{t("লোড হচ্ছে…", "Loading…")}</span>
        </Surface>
      ) : error ? (
        <Surface className="p-6 mb-7 border-red">
          <p className={`${font} text-[16px] text-ink mb-3`}>
            {t("ভর্তির তথ্য আনা যায়নি।", "Could not load the admission details.")}
          </p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            {t("আবার চেষ্টা করুন", "Try again")}
          </Button>
        </Surface>
      ) : (
        <Surface className="p-5 sm:p-6 mb-7">
          <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
            <h2 className={`${font} text-[19px] font-bold text-green-dark`}>
              {t(...(INSTITUTION_LABEL[institution] || [institution, institution]))}
            </h2>
            <span
              className={`${font} text-[13px] font-bold rounded-full px-3 py-1 ${
                isOpen ? "bg-green-light text-green-dark" : "bg-[#fdeaea] text-red"
              }`}
            >
              {isOpen ? t("আবেদন চলছে", "Open") : state === "scheduled" ? t("শীঘ্রই", "Opening soon") : t("আবেদন বন্ধ", "Closed")}
            </span>
          </div>

          {round ? (
            <div className="mt-3">
              <InfoRow label={t("শিক্ষাবর্ষ", "Session")}>{round.academicYear}</InfoRow>
              <InfoRow label={t("যেসব শ্রেণিতে", "Classes open")}>
                {(round.classes || []).map((c) => c.class).join(", ") ||
                  t("এখনো নির্ধারিত হয়নি", "Not yet set")}
              </InfoRow>
              {closesAt && (
                <InfoRow label={t("শেষ তারিখ", "Deadline")}>{fmtDate(closesAt, lang)}</InfoRow>
              )}
              {(round.classes || []).some((c) => c.fee) && (
                <InfoRow label={t("আবেদন ফি", "Application fee")}>
                  {(round.classes || [])
                    .filter((c) => c.fee)
                    .map((c) => `${c.class} — ${c.fee}`)
                    .join(" · ")}
                </InfoRow>
              )}
            </div>
          ) : (
            <p className={`${font} text-[16px] text-muted mt-3`}>
              {t(
                "এই মুহূর্তে কোনো ভর্তি কার্যক্রম চালু নেই। নোটিশ বোর্ডে চোখ রাখুন।",
                "No admission round is open right now. Please watch the notice board."
              )}
            </p>
          )}

          {!isOpen && round?.closedNote && (
            <p className={`${font} text-[15px] text-ink bg-cream rounded-xl p-3.5 mt-4`}>
              {round.closedNote}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button
              disabled={!isOpen}
              onClick={() => navigate(portalLink(institution, "/start"))}
              className="sm:flex-1"
            >
              {t("আবেদন শুরু করুন", "Start application")}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                navigate(draft ? "/admissions-portal/section" : "/admissions-portal/continue")
              }
              className="sm:flex-1"
            >
              {t("আবেদন চালিয়ে যান", "Continue application")}
            </Button>
          </div>
        </Surface>
      )}

      {/* --------------------------------------------------- keep these ready */}
      <Surface className="p-5 sm:p-6 mb-7">
        <h2 className={`${font} text-[19px] font-bold text-green-dark mb-1.5`}>
          {t("শুরুর আগে এগুলো হাতের কাছে রাখুন", "Keep these ready before you start")}
        </h2>
        <p className={`${font} text-[15px] text-muted leading-relaxed mb-4`}>
          {t(
            "আবেদন যেকোনো সময় সংরক্ষণ করে পরে ফিরে আসা যাবে — সব একবারে পূরণ করতে হবে না।",
            "You can save at any point and come back later — it does not have to be done in one sitting."
          )}
        </p>
        <ul className={`${font} text-[16px] text-ink space-y-2.5`}>
          {checklist.map(([bn, en]) => (
            <li key={en} className="flex gap-3">
              <span className="text-green shrink-0" aria-hidden="true">
                ✓
              </span>
              <span>{t(bn, en)}</span>
            </li>
          ))}
        </ul>
      </Surface>

      {/* ------------------------------------------------ supporting resources */}
      <div className="border-t border-line pt-5">
        <h2 className={`${font} text-[15px] font-bold text-muted uppercase tracking-wide mb-3`}>
          {t("সহায়ক ফাইল", "Supporting resources")}
        </h2>
        <div className="flex flex-col sm:flex-row gap-3">
          {institution === "kcgs" && <a
            href="/forms/admission-page-1.pdf"
            className={`${font} text-[15px] text-green inline-flex items-center gap-2 min-h-[44px] no-underline hover:underline`}
          >
            ↓ {t("খালি ভর্তি ফরম (পিডিএফ)", "Blank admission form (PDF)")}
          </a>}
          {institution === "kcgs" && <a href="/forms/admission-page-2.pdf" className="text-green underline py-3">{t("ফরম: দ্বিতীয় পৃষ্ঠা", "Blank form: page 2")}</a>}
          <Link
            to="/notices"
            className={`${font} text-[15px] text-green inline-flex items-center gap-2 min-h-[44px] no-underline hover:underline`}
          >
            {t("ভর্তি সংক্রান্ত নোটিশ", "Admission notices")} →
          </Link>
        </div>
      </div>

      <p className={`${font} text-[13.5px] text-muted mt-8`}>
        {t(
          "প্রশ্ন থাকলে বিদ্যালয় অফিসে যোগাযোগ করুন। ফোন ০২৪-৭৭৭২১৪২২।",
          "For help, contact the school office on 024-77721422."
        )}
        {isOpen && round?.classes?.length ? (
          <span className="block mt-1">
            {t(
              `এই বছর ${toBn(round.classes.length)} টি শ্রেণিতে আবেদন নেওয়া হচ্ছে।`,
              `${round.classes.length} classes are accepting applications this session.`
            )}
          </span>
        ) : null}
      </p>
    </PortalShell>
  );
}
